<template>
    <div id="workstation">
        <Loader size="2xl" indefinite />
        <div class="status">{{ status }}</div>
    </div>
</template>

<script lang="ts" setup>
import { OpenApiError, dataFrom } from '@zyno-io/openapi-client-codegen';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { AuthWorkstationsApi } from '@/openapi-clients-generated/auth';
import Loader from '@/shared/components/loader.vue';
import { Auth } from '@/shared/services/auth.service';

import { WorkstationBridge } from '../helpers/bridge';

const props = defineProps<{
    // eslint-disable-next-line no-explicit-any
    showError: (err?: any) => void;
}>();

const router = useRouter();

const status = ref('Checking in...');

async function checkIn() {
    // initialize the bridge before performing any other operation
    await WorkstationBridge.init();

    // this route should only be accessed by ZynoWorkstation w/ WebView2
    if (!WorkstationBridge.isWithinZynoWorkstation()) {
        return props.showError();
    }

    const handshake = await WorkstationBridge.bridgeFunctions?.generateHandshake();
    if (!handshake) {
        return props.showError();
    }

    try {
        const response = dataFrom(
            await AuthWorkstationsApi.postWorkstationsDetectWorkstation({
                body: { handshake }
            })
        );

        Auth.setWorkstation({ id: response.id });
        Auth.setTenant({ id: response.tenantId, name: response.tenantName, isAdmin: false });

        // A user-specific (bound) workstation comes back with a ready login session — sign in as the
        // linked user automatically. Shared workstations return none and fall through to the per-user
        // sign-in form once we continue to the app.
        if (response.session) {
            status.value = 'Signing in...';
            await Auth.processLoginResponse(response.session);
        }
    } catch (err) {
        if (err instanceof OpenApiError) {
            if (err.response?.status === 401) {
                // unregistered workstation — go to the in-app first screen
                // (enter a code, or sign in to register).
                return router.replace('/zw/start');
            }
        }
        return props.showError(err);
    }

    // run post-registration operations
    await WorkstationBridge.bridgeFunctions?.performPostRegistrationTasks();

    // continue as a normal web app
    router.replace('/');
}

onMounted(checkIn);
</script>

<style scoped>
@reference '#tw.css';

#workstation {
    @apply flex-1 flex flex-col items-center justify-center gap-8;
}

.status {
    @apply text-white text-xl font-semibold;
}
</style>
